import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { H2, H3 } from "@leafygreen-ui/typography";
import Button from "@leafygreen-ui/button";
import Icon from "@leafygreen-ui/icon";
import TextInput from "@leafygreen-ui/text-input";
import ExpandableCard from "@leafygreen-ui/expandable-card";
import ConfirmationModal from "@leafygreen-ui/confirmation-modal";
import Banner from "@leafygreen-ui/banner";
import { css } from "@leafygreen-ui/emotion";
import { useApi } from "../providers/Api";
import { useAuth0 } from "@auth0/auth0-react";
import EditorAccess from "../components/EditorAccess";
import AutoGrowTextArea from "../components/AutoGrowTextArea";
import { useSectionReorder, addItem, removeItem, updateItem } from "../lib/sectionList";
import { nameToSlug, tokenizeForTemplate } from "../lib/templateUtils";

const pageStyle = css`
  max-width: 860px;
  margin: 0 auto;
  padding-bottom: 80px;
`;

// Spacing between individual form fields (applied to the field wrapper, not
// the <input>, so it doesn't inflate LeafyGreen's input container).
const fieldStyle = css`
  margin-bottom: 20px;
`;

// The three top-level groups (Header, Page content, Additional Resources),
// plus the Internal block, each render as a distinct bordered panel.
const groupStyle = css`
  border: 1px solid #e8edeb;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  background: #ffffff;
`;

const subHeadingStyle = css`
  font-weight: 600;
  font-size: 14px;
  color: #1c2d38;
  margin: 24px 0 12px;
`;

const backLinkStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 20px 0 8px;
  color: #016bf8;
  text-decoration: none;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
  }
`;

const sectionCardStyle = css`
  margin: 16px 0;
  border-radius: 8px;
  transition: box-shadow 0.15s ease;
  button {
    margin-top: 20px;
  }
`;

const sectionCardOverStyle = css`
  box-shadow: 0 0 0 2px #00ed64;
`;

const dragHandleStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 4px;
  color: #889397;
  font-size: 12px;
  cursor: grab;
  user-select: none;
  &:active {
    cursor: grabbing;
  }
`;

const resourceCardStyle = css`
  border: 1px solid #e8edeb;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 12px 0;
  transition: box-shadow 0.15s ease;
`;

const resourceCardOverStyle = css`
  box-shadow: 0 0 0 2px #00ed64;
`;

const resourceFieldsStyle = css`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-top: 8px;
  > div {
    flex: 1;
    min-width: 0;
  }
  input {
    margin-bottom: 0;
  }
`;

const removeResourceStyle = css`
  margin-top: 16px;
`;

const dividerStyle = css`
  border: none;
  border-top: 1px solid #e8edeb;
  margin: 8px 0 16px;
`;

const footerStyle = css`
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid #e8edeb;
  padding: 16px 0;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

// Default additional resources for a brand-new landing page.
const DEFAULT_RESOURCES = [
  { title: "Stay connected, and join our Reddit community", link: "https://www.reddit.com/r/MongoDB_Official/" },
  { title: "Speak at a MongoDB meetup", link: "https://mdb.link/speaker" },
  { title: "Keep Earning Skill Badges", link: "https://learn.mongodb.com/skills?team=devrel" },
];

export default function LandingEditor({ mode }) {
  const { identifier: routeIdentifier } = useParams();
  const navigate = useNavigate();
  const { fetchLandings, insertLanding, updateLanding, lookupRoute, profile } = useApi();
  const { user } = useAuth0();

  const isEdit = mode === "edit";
  const isAdd = !isEdit; // create or clone

  const [loading, setLoading] = useState(mode !== "create");
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allIdentifiers, setAllIdentifiers] = useState([]);
  const [landingForEdit, setLandingForEdit] = useState(null);

  // Landing form
  const [identifier, setIdentifier] = useState("");
  const [identifierState, setIdentifierState] = useState("none");
  const [identifierError, setIdentifierError] = useState("You may only use lowercase letters, numbers, and hyphens.");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [summary, setSummary] = useState("");
  const [ctaLabel, setCtaLabel] = useState("Try MongoDB Now");
  const [ctaLink, setCtaLink] = useState("https://mongodb.com");
  const [otherSections, setOtherSections] = useState([{ title: "Other content", content: "" }]);
  const [additionalResources, setAdditionalResources] = useState(DEFAULT_RESOURCES.map(r => ({ ...r })));

  const [shortieConflict, setShortieConflict] = useState(null);
  const [resourcesOpen, setResourcesOpen] = useState(false); // Additional Resources collapsed by default

  // Create-template-from-event flow
  const [createTmplOpen, setCreateTmplOpen] = useState(false);
  const [newTmplName, setNewTmplName] = useState("");
  const [newTmplSlug, setNewTmplSlug] = useState("");
  const [newTmplSlugState, setNewTmplSlugState] = useState("none");
  const [newTmplSlugError, setNewTmplSlugError] = useState("You may only use lowercase letters, numbers, and hyphens.");

  const reorder = useSectionReorder(otherSections, setOtherSections);

  // Load landings for duplicate checks, and prefill in edit/clone mode.
  useEffect(() => {
    let active = true;
    (async () => {
      const results = await fetchLandings();
      if (!active) return;
      setAllIdentifiers((results || []).map(l => l.identifier));

      if (mode !== "create") {
        const landing = (results || []).find(l => l.identifier === routeIdentifier);
        if (!landing) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setLandingForEdit(landing);
        setIdentifier(isEdit ? landing.identifier : "");
        if (isEdit) setIdentifierState("valid");
        setTitle(landing.title || "");
        setSubtitle(landing.subtitle || "");
        setSummary(landing.summary || "");
        setCtaLabel(landing.ctaButton?.label ?? "Try MongoDB Now");
        setCtaLink(landing.ctaButton?.linkTo ?? "https://mongodb.com");
        setOtherSections(
          landing.otherSections?.length
            ? landing.otherSections.map(s => ({ ...s }))
            : [{ title: "Other content", content: "" }]
        );
        setAdditionalResources(
          landing.additionalResources?.length
            ? landing.additionalResources.map(r => ({ ...r }))
            : []
        );
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [mode, routeIdentifier, isEdit, fetchLandings]);

  // Shortie-conflict check (add/clone only, once the identifier validates).
  useEffect(() => {
    if (!isAdd || !identifier || identifierState !== "valid") {
      setShortieConflict(null);
      return;
    }
    const timer = setTimeout(async () => {
      const existing = await lookupRoute(`/${identifier}`);
      if (existing && !existing.to?.includes(`landing.mdb.link/${identifier}`)) {
        setShortieConflict(existing.to);
      } else {
        setShortieConflict(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [identifier, identifierState, isAdd, lookupRoute]);

  const handleIdentifierChange = value => {
    if (!value.match(/^[a-z0-9-]+$/)) {
      setIdentifierState("error");
      setIdentifierError("You may only use lowercase letters, numbers, and hyphens.");
    } else if (isAdd && allIdentifiers.includes(value)) {
      setIdentifierState("error");
      setIdentifierError("A landing page with this identifier already exists.");
    } else {
      setIdentifierState("valid");
    }
    setIdentifier(value);
  };

  // Section handlers (shared array helpers).
  const addSection = () => setOtherSections(addItem(otherSections, { title: "New Section", content: "" }));
  const removeSection = (index) => setOtherSections(removeItem(otherSections, index));
  const changeSectionTitle = (index, value) => setOtherSections(updateItem(otherSections, index, { title: value }));
  const changeSectionContent = (index, value) => setOtherSections(updateItem(otherSections, index, { content: value }));

  // Additional resources handlers (shared array helpers + reorder hook).
  const resourceReorder = useSectionReorder(additionalResources, setAdditionalResources);
  const addResource = () => setAdditionalResources(addItem(additionalResources, { title: "", link: "" }));
  const removeResource = (index) => setAdditionalResources(removeItem(additionalResources, index));
  const changeResourceTitle = (index, value) => setAdditionalResources(updateItem(additionalResources, index, { title: value }));
  const changeResourceLink = (index, value) => setAdditionalResources(updateItem(additionalResources, index, { link: value }));

  const save = async () => {
    setSaving(true);
    const ctaButton = { label: ctaLabel, linkTo: ctaLink };
    const landing = { title, subtitle, summary, ctaButton, otherSections, additionalResources, identifier };
    if (isEdit) {
      await updateLanding(identifier, landing);
    } else {
      await insertLanding(landing);
    }
    navigate("/app/landings");
  };

  // Create-template-from-event handlers.
  const validateTmplSlug = (value) => {
    if (!value || !value.match(/^[a-z0-9-]+$/)) {
      setNewTmplSlugState("error");
      setNewTmplSlugError("You may only use lowercase letters, numbers, and hyphens.");
    } else if (allIdentifiers.includes(value)) {
      setNewTmplSlugState("error");
      setNewTmplSlugError("A landing page with this identifier already exists.");
    } else {
      setNewTmplSlugState("valid");
    }
  };

  const openCreateTemplate = () => {
    setNewTmplName("");
    setNewTmplSlug("");
    setNewTmplSlugState("none");
    setCreateTmplOpen(true);
  };

  const handleNewTmplNameChange = (value) => {
    setNewTmplName(value);
    const slug = nameToSlug(value);
    setNewTmplSlug(slug);
    validateTmplSlug(slug);
  };

  const createTemplateFromEvent = async () => {
    const currentData = {
      title, subtitle, summary,
      ctaButton: { label: ctaLabel, linkTo: ctaLink },
      otherSections, additionalResources,
    };
    const tokenized = tokenizeForTemplate(currentData);
    tokenized.identifier = newTmplSlug;
    tokenized.templateName = newTmplName;
    await insertLanding(tokenized, { skipShortie: true });
    setCreateTmplOpen(false);
    navigate("/app/landings");
  };

  const heading = isEdit ? "Update a landing page" : (mode === "clone" ? "Clone a landing page" : "Create a landing page");

  if (loading) {
    return (
      <React.Fragment>
        <Link to="/app/landings" className={backLinkStyle}>
          <Icon glyph="ArrowLeft" /> Back to landing pages
        </Link>
        <p>Loading…</p>
      </React.Fragment>
    );
  }

  if (notFound) {
    return (
      <React.Fragment>
        <Link to="/app/landings" className={backLinkStyle}>
          <Icon glyph="ArrowLeft" /> Back to landing pages
        </Link>
        <Banner variant="danger" style={{ marginTop: "16px" }}>
          No landing page found with identifier <strong>{routeIdentifier}</strong>.
        </Banner>
      </React.Fragment>
    );
  }

  const saveDisabled = saving || (isAdd && (identifierState !== "valid" || shortieConflict !== null));

  return (
    <div className={pageStyle}>
      <Link to="/app/landings" className={backLinkStyle}>
        <Icon glyph="ArrowLeft" /> Back to landing pages
      </Link>
      <H2>{heading}</H2>

      <form>
        {isEdit && landingForEdit && (profile?.isAdmin === true || (Array.isArray(landingForEdit.owner)
          ? landingForEdit.owner.includes(user?.sub)
          : landingForEdit.owner === user?.sub)) && (
          <EditorAccess resourceType="landings" resourceId={identifier} />
        )}
        <section className={groupStyle}>
          <H3>Unique URL</H3>
          <TextInput
            className={fieldStyle}
            label="Page identifier, matched by a shorlink"
            value={identifier}
            errorMessage={identifierError}
            state={identifierState}
            disabled={isEdit}
            onChange={e => handleIdentifierChange(e.target.value)}
          />
          {shortieConflict && (
            <Banner variant="danger" style={{ marginTop: "12px" }}>
              A shortie <strong>mdb.link/{identifier}</strong> already exists pointing to <strong>{shortieConflict}</strong>. A matching shortie will not be created automatically.
            </Banner>
          )}
        </section>

        <section className={groupStyle}>
          <H3>Header</H3>
          <TextInput className={fieldStyle} label="Title" value={title} onChange={e => setTitle(e.target.value)} />
          <TextInput className={fieldStyle} optional={true} label="Subtitle" value={subtitle} onChange={e => setSubtitle(e.target.value)} />
          <AutoGrowTextArea className={fieldStyle} label="Summary" value={summary} onChange={e => setSummary(e.target.value)} />
          <div className={subHeadingStyle}>Call to action button</div>
          <TextInput className={fieldStyle} label="Label" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} />
          <TextInput className={fieldStyle} label="Link To" value={ctaLink} onChange={e => setCtaLink(e.target.value)} />
        </section>

        <section className={groupStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <H3 style={{ display: "inline-block" }}>Page content</H3>
            <Button onClick={addSection} variant="primaryOutline" leftGlyph={<Icon glyph="Plus" />}>Add Section</Button>
          </div>
          {otherSections.length > 1 && (
            <p style={{ color: "#889397", fontSize: "13px", margin: "8px 0 0" }}>
              Drag the handle at the top of a section to reorder.
            </p>
          )}
          {otherSections.map((section, index) => (
            <div
              key={index}
              className={`${sectionCardStyle}${reorder.dragOverIndex === index ? ` ${sectionCardOverStyle}` : ''}`}
              onDragOver={(e) => reorder.handleDragOver(e, index)}
              onDrop={(e) => reorder.handleDrop(e, index)}
            >
              <div
                className={dragHandleStyle}
                draggable
                onDragStart={() => reorder.handleDragStart(index)}
                onDragEnd={reorder.handleDragEnd}
              >
                <Icon glyph="Drag" /> Drag to reorder
              </div>
              <ExpandableCard title={section.title || "Untitled section"}>
                <TextInput className={fieldStyle} label="Section Title" value={section.title} onChange={(e) => changeSectionTitle(index, e.target.value)} />
                <AutoGrowTextArea
                  className={fieldStyle}
                  label="Summary"
                  description="Support for full markdown. Use H3 (### section title) for sub headers."
                  value={section.content}
                  onChange={(e) => changeSectionContent(index, e.target.value)}
                />
                <Button onClick={() => removeSection(index)} variant="dangerOutline" leftGlyph={<Icon glyph="Trash" />}>Remove Section</Button>
              </ExpandableCard>
            </div>
          ))}
        </section>

        <section className={groupStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div
              onClick={() => setResourcesOpen(o => !o)}
              style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
            >
              <Icon glyph={resourcesOpen ? "ChevronDown" : "ChevronRight"} fill="#889397" />
              <H3 style={{ display: "inline-block", margin: 0 }}>Additional Resources</H3>
              <span style={{ color: "#889397", fontSize: "13px" }}>({additionalResources.length})</span>
            </div>
            <Button onClick={() => { setResourcesOpen(true); addResource(); }} variant="primaryOutline" leftGlyph={<Icon glyph="Plus" />}>Add Resource</Button>
          </div>
          {resourcesOpen && additionalResources.length > 1 && (
            <p style={{ color: "#889397", fontSize: "13px", margin: "8px 0 0" }}>
              Drag the handle to reorder. These feed the Additional Resources carousel on the page.
            </p>
          )}
          {resourcesOpen && additionalResources?.map((resource, index) => (
            <div
              key={index}
              className={`${resourceCardStyle}${resourceReorder.dragOverIndex === index ? ` ${resourceCardOverStyle}` : ''}`}
              onDragOver={(e) => resourceReorder.handleDragOver(e, index)}
              onDrop={(e) => resourceReorder.handleDrop(e, index)}
            >
              <div
                className={dragHandleStyle}
                draggable
                onDragStart={() => resourceReorder.handleDragStart(index)}
                onDragEnd={resourceReorder.handleDragEnd}
              >
                <Icon glyph="Drag" /> Drag to reorder
              </div>
              <div className={resourceFieldsStyle}>
                <TextInput label="Title" value={resource.title} onChange={(e) => changeResourceTitle(index, e.target.value)} />
                <TextInput label="Link" value={resource.link} onChange={(e) => changeResourceLink(index, e.target.value)} />
              </div>
              <Button className={removeResourceStyle} onClick={() => removeResource(index)} variant="dangerOutline" leftGlyph={<Icon glyph="Trash" />}>Remove Resource</Button>
            </div>
          ))}
        </section>

        {isEdit && (
          <section>
            <hr className={dividerStyle} />
            <Button variant="primaryOutline" leftGlyph={<Icon glyph="Sparkle" />} onClick={openCreateTemplate}>
              Create template from this event
            </Button>
          </section>
        )}
      </form>

      <div className={footerStyle}>
        <Button onClick={() => navigate("/app/landings")}>Cancel</Button>
        <Button variant="primary" disabled={saveDisabled} onClick={save} leftGlyph={<Icon glyph="Save" />}>
          Save
        </Button>
      </div>

      {/* Create template from this event */}
      <ConfirmationModal
        open={createTmplOpen}
        onCancel={() => setCreateTmplOpen(false)}
        title="Create a template from this event"
        buttonText="Create Template"
        onConfirm={createTemplateFromEvent}
        submitDisabled={newTmplSlugState !== "valid" || !newTmplName.trim()}
      >
        <p style={{ marginBottom: "16px", color: "#5c6c75" }}>
          The subtitle will become <code>{"{{date}}"}</code> and Google Slides URLs in each section will become named tokens. Everything else is copied as-is.
        </p>
        <TextInput
          label="Template name"
          description="Shown in the template picker."
          value={newTmplName}
          onChange={e => handleNewTmplNameChange(e.target.value)}
          style={{ marginBottom: "16px" }}
        />
        <TextInput
          label="Template identifier"
          description="URL-safe slug. Auto-derived from the name."
          value={newTmplSlug}
          state={newTmplSlugState}
          errorMessage={newTmplSlugError}
          onChange={e => { setNewTmplSlug(e.target.value); validateTmplSlug(e.target.value); }}
        />
      </ConfirmationModal>
    </div>
  );
}
